import { cloneElement, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Flex, DropdownDownIcon } from 'src/components';

const AccordionStyle = styled.div((props: { style: React.CSSProperties }) => ({
  width: '100%',
  ...props.style
}))

const AccordionHeaderStyle = styled.div((props: { expandable: boolean }) => ({
  width: '100%',
  cursor: props.expandable ? 'pointer' : 'default'
}))

const AccordionBodyStyle = styled.div((props: { isOpen: boolean, maxHeight: number | null, gap: number | string }) => ({
  // eslint-disable-next-line no-nested-ternary
  maxHeight: props.isOpen ? (props.maxHeight ? `${props.maxHeight}px` : 'auto') : '0px',
  overflow: 'hidden',
  transition: 'max-height 0.3s ease, margin-top 0.6s ease',
  marginTop: props.isOpen ? props.gap : 0,
}))

const FlexItem = styled.div({
  flex: 1
})

const FlexItem2 = styled.div({
  flex: 0,
  alignSelf: 'center',
})

export interface AccordionProps {
  title: string | React.JSX.Element | React.JSX.Element[];
  content: string | React.JSX.Element | React.JSX.Element[];
  key?: number;
  style?: React.CSSProperties,
  icon?: React.ReactNode;
  expandIcon?: boolean;
  expandable?: boolean;
  open?: boolean;
  gap?: number | string;
  onChange?: () => void;
  expandIconComponent?: React.ReactElement;
  maxHeight?: number;
  className?: string;
  isLazy?: boolean;
}

export const Accordion = ({ style = {}, icon, title, content, open = false, expandable = true, expandIcon = true, gap = 0, onChange, expandIconComponent = <DropdownDownIcon />, maxHeight, className, isLazy = false }: AccordionProps) => {
  const [isOpen, setIsOpen] = useState(open);
  const [maxHeightValue, setMaxHeightValue] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    if (contentRef.current) {
      setMaxHeightValue(contentRef.current.scrollHeight);
    }
  }, [content, contentRef.current?.scrollHeight]);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
    if (onChange) onChange();
  };

  return (
    <AccordionStyle style={style} className={className}>
      <AccordionHeaderStyle onClick={() => expandable && toggleAccordion()} expandable={expandable}>
        <Flex justify='space-between'>
          {icon ? <>{icon}</> : <></>}
          <FlexItem>
            {title}
          </FlexItem>
          {expandIcon ? <FlexItem2>{cloneElement(expandIconComponent, { isOpen })}</FlexItem2> : <></>}
        </Flex>
      </AccordionHeaderStyle>
      <AccordionBodyStyle isOpen={isOpen} maxHeight={maxHeight || maxHeightValue} ref={contentRef} gap={gap}>
        {!isLazy || isOpen ? content : null}
      </AccordionBodyStyle>
    </AccordionStyle>
  );
};
